import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../services/api';
import {
    Box, Typography, Paper, Grid, Card, Button, CircularProgress, Alert,
    Fade, Grow, Chip, TextField, LinearProgress,
} from '@mui/material';
import { AccountBalance, Sync, CreditCard, Add } from '@mui/icons-material';
import { usePlaidLink } from 'react-plaid-link';
import { plaidAccountFilterLabel, dedupePlaidLinkedAccounts } from '../utils/plaidAccountDisplay';

const D = {
    bg: '#13141a',
    card: '#1c1d26',
    border: '#353849',
    fg: '#f2f2f5',
    muted: '#8b8d98',
    primary: '#7387ff',
    primaryFg: '#fafafa',
    secondary: 'rgba(255,255,255,0.06)',
};

const fmt = (v) => parseFloat(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (v) => Number(v || 0).toLocaleString('en-US');
const fmtClock = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' });
};
const plural = (n, one, many = `${one}s`) => `${fmtInt(n)} ${Number(n) === 1 ? one : many}`;

/**
 * Plaid “Add account” + connected banks list for Settings → Connections.
 */
function PlaidConnectionsSettings() {
    const [institutions, setInstitutions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState(null);
    const [syncingKey, setSyncingKey] = useState(null);
    const [plaidToken, setPlaidToken] = useState(null);
    const [connecting, setConnecting] = useState(false);
    const [syncSummary, setSyncSummary] = useState(null);
    /** @type {Record<number, string>} */
    const [displayNameDraft, setDisplayNameDraft] = useState({});
    const [savingAccountId, setSavingAccountId] = useState(null);
    const openedPlaidTokenRef = useRef(null);
    const exchangedPublicTokensRef = useRef(new Set());

    const loadInstitutions = useCallback(() => {
        setLoading(true);
        api.get('/api/plaid/institutions')
            .then(res => setInstitutions(res.data || []))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { loadInstitutions(); }, [loadInstitutions]);

    useEffect(() => {
        const next = {};
        for (const inst of institutions) {
            for (const a of inst.accounts || []) {
                next[a.id] = a.displayName != null ? String(a.displayName) : '';
            }
        }
        setDisplayNameDraft(next);
    }, [institutions]);

    const displayInstitutions = useMemo(() => {
        const byName = new Map();
        for (const inst of institutions) {
            const key = (inst.name || '').trim().toLowerCase() || `_id_${inst.id}`;
            const cur = byName.get(key);
            if (!cur) {
                byName.set(key, {
                    name: inst.name,
                    accounts: [...(inst.accounts || [])],
                    connectionIds: [inst.id],
                });
            } else {
                cur.connectionIds.push(inst.id);
                const seenIds = new Set(cur.accounts.map((a) => a.id));
                for (const a of inst.accounts || []) {
                    if (!seenIds.has(a.id)) {
                        seenIds.add(a.id);
                        cur.accounts.push(a);
                    }
                }
            }
        }
        return Array.from(byName.values()).map((cur) => ({
            ...cur,
            accounts: dedupePlaidLinkedAccounts(
                (cur.accounts || []).map((a) => ({ ...a, institutionName: cur.name })),
            ),
        }));
    }, [institutions]);

    const handleConnectBank = async () => {
        if (connecting || plaidToken) return;
        try {
            setAlert(null);
            const { data } = await api.post('/api/plaid/link-token', { userId: 'default-user' });
            setPlaidToken(data.link_token);
        } catch (err) {
            setAlert({ severity: 'error', message: 'Failed to start bank connection: ' + (err.response?.data?.error || err.message) });
        }
    };

    const onPlaidSuccess = useCallback(async (publicToken, metadata) => {
        if (!publicToken || exchangedPublicTokensRef.current.has(publicToken)) return;
        exchangedPublicTokensRef.current.add(publicToken);
        const institutionName = metadata.institution?.name || 'bank';
        setConnecting(true);
        setAlert(null);
        setSyncSummary({
            status: 'running',
            kind: 'connect',
            institutionName,
            startedAt: new Date().toISOString(),
            completedAt: null,
            connectionCount: 1,
            accountCount: null,
            transactionsSynced: null,
            results: [],
        });
        try {
            await api.post('/api/plaid/connect', {
                publicToken,
                institutionId: metadata.institution?.institution_id || '',
                institutionName,
            });
            setAlert({ severity: 'success', message: `Connected ${institutionName} and ran initial sync.` });
            setSyncSummary((prev) => ({
                ...(prev || {}),
                status: 'success',
                institutionName,
                completedAt: new Date().toISOString(),
            }));
            setPlaidToken(null);
            openedPlaidTokenRef.current = null;
            loadInstitutions();
        } catch (err) {
            const message = err.response?.data?.error || err.message;
            setAlert({ severity: 'error', message: 'Failed to connect: ' + message });
            setSyncSummary((prev) => ({
                ...(prev || {}),
                status: 'error',
                institutionName,
                completedAt: new Date().toISOString(),
                error: message,
            }));
        } finally {
            setConnecting(false);
        }
    }, [loadInstitutions]);

    const { open: openPlaid, ready: plaidReady } = usePlaidLink({
        token: plaidToken,
        onSuccess: onPlaidSuccess,
        onExit: () => {
            setPlaidToken(null);
            openedPlaidTokenRef.current = null;
        },
    });

    useEffect(() => {
        if (!plaidToken) {
            openedPlaidTokenRef.current = null;
            return;
        }
        if (plaidReady && openedPlaidTokenRef.current !== plaidToken) {
            openedPlaidTokenRef.current = plaidToken;
            openPlaid();
        }
    }, [plaidToken, plaidReady, openPlaid]);

    const handleResync = async (connectionIds, instName) => {
        const ids = Array.isArray(connectionIds) ? connectionIds : [connectionIds];
        const key = [...ids].sort((a, b) => a - b).join(',');
        setSyncingKey(key);
        setAlert(null);
        setSyncSummary({
            status: 'running',
            kind: 'sync',
            institutionName: instName,
            startedAt: new Date().toISOString(),
            completedAt: null,
            connectionCount: ids.length,
            accountCount: 0,
            transactionsSynced: 0,
            results: [],
        });
        try {
            let total = 0;
            let accountCount = 0;
            const results = [];
            for (const instId of ids) {
                const { data } = await api.post(`/api/plaid/sync/${instId}`);
                const accounts = Number(data.accounts || 0);
                const transactionsSynced = Number(data.transactionsSynced || 0);
                accountCount += accounts;
                total += transactionsSynced;
                results.push({ institutionId: instId, accounts, transactionsSynced });
            }
            const suffix = ids.length > 1 ? ` (${ids.length} connections)` : '';
            setAlert({ severity: 'success', message: `Synced ${instName}${suffix}: ${total} new transactions` });
            setSyncSummary((prev) => ({
                ...(prev || {}),
                status: 'success',
                completedAt: new Date().toISOString(),
                accountCount,
                transactionsSynced: total,
                results,
            }));
            loadInstitutions();
        } catch (err) {
            const message = err.response?.data?.error || err.message;
            setAlert({ severity: 'error', message: `Sync failed: ${message}` });
            setSyncSummary((prev) => ({
                ...(prev || {}),
                status: 'error',
                completedAt: new Date().toISOString(),
                error: message,
            }));
        } finally {
            setSyncingKey(null);
        }
    };

    const saveAccountDisplayName = async (accountId, rawValue) => {
        const trimmed = (rawValue ?? '').trim();
        setSavingAccountId(accountId);
        setAlert(null);
        try {
            await api.patch(`/api/plaid/accounts/${accountId}`, {
                displayName: trimmed.length > 0 ? trimmed : '',
            });
            await loadInstitutions();
        } catch (err) {
            setAlert({
                severity: 'error',
                message: `Could not save account label: ${err.response?.data?.error || err.message}`,
            });
        } finally {
            setSavingAccountId(null);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: '#667eea' }} />
            </Box>
        );
    }

    return (
        <Box>
            {alert && (
                <Fade in>
                    <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{ mb: 2, borderRadius: 2 }}>
                        {alert.message}
                    </Alert>
                </Fade>
            )}
            {syncSummary && (
                <Fade in>
                    <Paper elevation={0} sx={{
                        mb: 2,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: D.card,
                        color: D.fg,
                        border: `1px solid ${syncSummary.status === 'error' ? '#fb7185' : D.border}`,
                    }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                            <Box>
                                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                    {syncSummary.status === 'running' && <CircularProgress size={16} sx={{ color: D.primary }} />}
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: D.fg }}>
                                        {syncSummary.status === 'running'
                                            ? `${syncSummary.kind === 'connect' ? 'Connecting and syncing' : 'Syncing'} ${syncSummary.institutionName}`
                                            : syncSummary.status === 'error'
                                                ? `Sync failed for ${syncSummary.institutionName}`
                                                : `${syncSummary.kind === 'connect' ? 'Connection sync completed' : 'Sync completed'} for ${syncSummary.institutionName}`}
                                    </Typography>
                                    <Chip
                                        label={syncSummary.status === 'running' ? 'In progress' : syncSummary.status === 'error' ? 'Failed' : 'Done'}
                                        size="small"
                                        sx={{
                                            height: 22,
                                            fontSize: '0.7rem',
                                            bgcolor: syncSummary.status === 'error' ? 'rgba(251,113,133,0.14)' : `${D.primary}22`,
                                            color: syncSummary.status === 'error' ? '#fb7185' : D.primary,
                                        }}
                                    />
                                </Box>
                                <Typography sx={{ mt: 0.75, fontSize: '0.78rem', color: D.muted }}>
                                    Started {fmtClock(syncSummary.startedAt)}
                                    {syncSummary.completedAt ? ` · Finished ${fmtClock(syncSummary.completedAt)}` : ''}
                                </Typography>
                                {syncSummary.error ? (
                                    <Typography sx={{ mt: 1, fontSize: '0.82rem', color: '#fb7185' }}>
                                        {syncSummary.error}
                                    </Typography>
                                ) : (
                                    <Typography sx={{ mt: 1, fontSize: '0.82rem', color: D.fg }}>
                                        {syncSummary.kind === 'connect' && syncSummary.status !== 'running'
                                            ? 'Plaid connected the bank and completed the initial account/transaction sync.'
                                            : `${plural(syncSummary.connectionCount || 1, 'connection')} checked`
                                                + (syncSummary.accountCount != null ? ` · ${plural(syncSummary.accountCount, 'account')} refreshed` : '')
                                                + (syncSummary.transactionsSynced != null ? ` · ${plural(syncSummary.transactionsSynced, 'new transaction')} imported` : '')}
                                    </Typography>
                                )}
                            </Box>
                            <Button size="small" onClick={() => setSyncSummary(null)}
                                sx={{ color: D.muted, textTransform: 'none', minWidth: 0 }}>
                                Dismiss
                            </Button>
                        </Box>
                        {syncSummary.status === 'running' && (
                            <LinearProgress sx={{
                                mt: 1.5,
                                bgcolor: D.secondary,
                                '& .MuiLinearProgress-bar': { bgcolor: D.primary },
                            }} />
                        )}
                    </Paper>
                </Fade>
            )}

            <Paper elevation={0} sx={{
                borderRadius: 3,
                overflow: 'hidden',
                bgcolor: D.bg,
                color: D.fg,
                border: `1px solid ${D.border}`,
            }}>
                <Box sx={{
                    px: 2.5, py: 2,
                    borderBottom: `1px solid ${D.border}`,
                    display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                }}>
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: D.fg }}>
                            Connected banks
                        </Typography>
                        <Typography sx={{ fontSize: '0.8rem', color: D.muted, mt: 0.5 }}>
                            Add accounts via Plaid, then sync to refresh balances and transactions. Give each account
                            a short label if you want a friendlier name in the Transactions filter.
                        </Typography>
                    </Box>
                    <Button variant="contained" onClick={handleConnectBank} disabled={connecting || Boolean(plaidToken)}
                        startIcon={connecting ? <CircularProgress size={16} sx={{ color: D.primaryFg }} /> : <Add />}
                        sx={{
                            bgcolor: D.primary, color: D.primaryFg, fontWeight: 600, textTransform: 'none',
                            borderRadius: 2, boxShadow: 'none',
                            '&:hover': { bgcolor: '#8b9cff', boxShadow: 'none' },
                        }}>
                        {connecting ? 'Connecting...' : 'Add account'}
                    </Button>
                </Box>

                <Box sx={{ p: 2.5 }}>
                    {institutions.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <AccountBalance sx={{ fontSize: 48, color: D.muted, mb: 1.5 }} />
                            <Typography sx={{ fontSize: '0.95rem', color: D.muted, mb: 2 }}>
                                No banks linked yet. Use Add account to connect via Plaid.
                            </Typography>
                            <Button variant="contained" onClick={handleConnectBank} disabled={connecting || Boolean(plaidToken)}
                                startIcon={<Add />}
                                sx={{
                                    bgcolor: D.primary, color: D.primaryFg, fontWeight: 600, textTransform: 'none',
                                    borderRadius: 2, boxShadow: 'none',
                                    '&:hover': { bgcolor: '#8b9cff', boxShadow: 'none' },
                                }}>
                                Link your first account
                            </Button>
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            {displayInstitutions.map((inst) => {
                                const rowSyncKey = [...inst.connectionIds].sort((a, b) => a - b).join(',');
                                const isSyncing = syncingKey === rowSyncKey;
                                return (
                                    <Grid item xs={12} md={6} key={rowSyncKey}>
                                        <Grow in timeout={400}>
                                            <Card elevation={0} sx={{
                                                borderRadius: 2, overflow: 'hidden',
                                                bgcolor: D.card, border: `1px solid ${D.border}`,
                                            }}>
                                                <Box sx={{
                                                    px: 2, py: 1.5,
                                                    borderBottom: `1px solid ${D.border}`,
                                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                    bgcolor: D.secondary,
                                                }}>
                                                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                                        <AccountBalance sx={{ color: D.primary, fontSize: 22 }} />
                                                        <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: D.fg }}>{inst.name}</Typography>
                                                        {inst.connectionIds.length > 1 && (
                                                            <Chip label={`${inst.connectionIds.length} links`} size="small"
                                                                sx={{
                                                                    height: 22, fontSize: '0.65rem', fontWeight: 600,
                                                                    bgcolor: `${D.primary}22`, color: D.primary, border: 'none',
                                                                }} />
                                                        )}
                                                    </Box>
                                                    <Button size="small" onClick={() => handleResync(inst.connectionIds, inst.name)}
                                                        disabled={isSyncing}
                                                        startIcon={isSyncing ? <CircularProgress size={14} sx={{ color: D.primary }} /> : <Sync sx={{ fontSize: 16 }} />}
                                                        sx={{
                                                            color: D.primary, textTransform: 'none', fontWeight: 600, fontSize: '0.75rem',
                                                            minWidth: 0,
                                                        }}>
                                                        {isSyncing ? 'Syncing...' : 'Sync'}
                                                    </Button>
                                                </Box>
                                                <Box sx={{ p: 1.5 }}>
                                                    {(inst.accounts || []).map(acct => (
                                                        <Box key={acct.id}
                                                            sx={{
                                                                py: 1.5,
                                                                borderBottom: `1px solid ${D.border}`,
                                                                '&:last-child': { borderBottom: 0 },
                                                            }}>
                                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                                                                <Box display="flex" alignItems="flex-start" gap={1} flex={1} minWidth={0}>
                                                                    <CreditCard sx={{ fontSize: 18, color: D.muted, mt: 0.25 }} />
                                                                    <Box flex={1} minWidth={0}>
                                                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: D.fg }}>
                                                                            {plaidAccountFilterLabel(acct, inst.accounts || [])}
                                                                        </Typography>
                                                                        <Typography sx={{ fontSize: '0.7rem', color: D.muted }}>
                                                                            Bank name: {acct.name}
                                                                            {acct.mask ? ` · •••• ${acct.mask}` : ''}
                                                                        </Typography>
                                                                        <TextField
                                                                            size="small"
                                                                            fullWidth
                                                                            label="Your label (optional)"
                                                                            placeholder="e.g. Personal checking"
                                                                            value={displayNameDraft[acct.id] ?? ''}
                                                                            onChange={(e) => setDisplayNameDraft((prev) => ({
                                                                                ...prev,
                                                                                [acct.id]: e.target.value,
                                                                            }))}
                                                                            onBlur={(e) => {
                                                                                const draft = e.target.value.trim();
                                                                                const saved = (acct.displayName ?? '').trim();
                                                                                if (draft !== saved) {
                                                                                    saveAccountDisplayName(acct.id, e.target.value);
                                                                                }
                                                                            }}
                                                                            disabled={savingAccountId === acct.id}
                                                                            inputProps={{ maxLength: 128 }}
                                                                            sx={{
                                                                                mt: 1,
                                                                                '& .MuiOutlinedInput-root': {
                                                                                    bgcolor: D.secondary,
                                                                                    fontSize: '0.85rem',
                                                                                    color: D.fg,
                                                                                },
                                                                                '& .MuiOutlinedInput-notchedOutline': { borderColor: D.border },
                                                                                '& .MuiInputLabel-root': {
                                                                                    color: D.muted,
                                                                                    fontSize: '0.75rem',
                                                                                },
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                </Box>
                                                                <Box textAlign="right" flexShrink={0}>
                                                                    <Chip label={acct.type} size="small" sx={{
                                                                        height: 22, fontSize: '0.65rem', fontWeight: 500,
                                                                        bgcolor: D.secondary, color: D.muted, border: `1px solid ${D.border}`,
                                                                    }} />
                                                                    {acct.currentBalance != null && (
                                                                        <Typography sx={{ fontSize: '0.72rem', display: 'block', fontWeight: 600, color: D.fg, mt: 0.5 }}>
                                                                            ${fmt(acct.currentBalance)}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Card>
                                        </Grow>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </Box>
            </Paper>
        </Box>
    );
}

export default PlaidConnectionsSettings;
